import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { MediaStreamSilentTrack, setWorkerInterval } from "@kalyzee/kast-web-sdk/web-components";
import { getAudioContextForVolumeAnalyzer } from "./audio";

/**
 * Name of the media carrying our screen share. Distinct from the sources'
 * names (a configured source may well be called "screen"), which is what tells
 * the share apart from the medias the camera and mic toggles drive.
 */
export const SCREEN_SHARE_MEDIA_NAME = "screenshare";

export const isScreenShareMedia = (media: Pick<MeetingSessionMedia, "name"> | undefined): boolean => media?.name === SCREEN_SHARE_MEDIA_NAME;

/** Our medias the camera and mic toggles drive: all of them but the screen share. */
export const getTogglableMedias = (session: MeetingSession | undefined): MeetingSessionMedia[] => (
  session?.medias?.filter((m) => !isScreenShareMedia(m)) ?? []
);

/**
 * Selector for the camera and mic toggles. Without a share, the plain outgoing
 * one, which also sets the manager defaults for the medias to come; with a
 * share, the other medias by id, so the share keeps its tracks.
 */
export const getTogglableMediasSelector = (session: MeetingSession) => {
  const medias = getTogglableMedias(session);
  if (medias.length === session.medias.length) return { direction: "outgoing" as const };
  return { sessions: [{ id: session.id, mediaIds: medias.map((m) => m.id) }] };
};

/**
 * `mode: "some"` over the togglable medias. With a share, read per media: the
 * group-wide reading would count the share, always on.
 */
export const togglableMediasEnabled = (
  manager: MeetingContainerAPI["emissionManager"],
  session: MeetingSession,
  kind: "audio" | "video",
): boolean => {
  const isEnabled = (options: { mediaSessionId?: string; mode?: "some" }) => (
    kind === "video" ? manager.videoIsEnabled(options) : manager.audioIsEnabled(options)
  );
  const medias = getTogglableMedias(session);
  if (medias.length === session.medias.length) return isEnabled({ mode: "some" });
  return medias.some((m) => isEnabled({ mediaSessionId: m.id }));
};

const DISPLAY_MEDIA_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: { width: { max: 1920 }, height: { max: 1080 }, frameRate: { ideal: 30 } },
  audio: { echoCancellation: { ideal: false }, autoGainControl: { ideal: false }, noiseSuppression: { ideal: false } },
};

const SCREEN_SHARE_FPS = 20;

/**
 * Whether this runtime can capture a screen. Mobile browsers lack
 * `getDisplayMedia` altogether; Electron exposes it but rejects every call
 * unless the shell installs a display-media handler, which ours does not — its
 * user agent is the only way to tell.
 */
export const canShareScreen = (): boolean => {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.mediaDevices?.getDisplayMedia !== "function") return false;
  if (/\bElectron\//.test(navigator.userAgent)) return false;
  return true;
};

/**
 * A screen capture, redrawn on a canvas at a steady rate. A display track only
 * emits frames when the screen changes, so a still slide would leave the
 * encoder starving; the canvas stream always has frames. The drawing runs on a
 * worker timer, which keeps ticking while our tab sits in the background — the
 * usual case, since the user is presenting another window.
 *
 * The published stream always has an audio track (a silent one when the
 * capture brings none): a media without one is reported as `NO_AUDIO_TRACK`.
 */
export class ScreenCapture {
  readonly stream: MediaStream;

  private readonly input: MediaStream;
  private readonly video: HTMLVideoElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private silentTrack?: MediaStreamSilentTrack;
  private drawTask?: { clear: () => void };

  /** Called once when the user stops the share from the browser UI. */
  onEnded?: () => void;

  /** Asks the user what to share. Rejects when they cancel the picker. */
  static async start(): Promise<ScreenCapture> {
    const input = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_CONSTRAINTS);
    return new ScreenCapture(input);
  }

  private constructor(input: MediaStream) {
    this.input = input;

    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d")!;
    this.stream = this.canvas.captureStream();

    this.video = document.createElement("video");
    this.video.muted = true;
    this.video.srcObject = input;
    this.video.play().catch(() => {});

    input.getAudioTracks().forEach((track) => this.stream.addTrack(track));
    if (!this.stream.getAudioTracks().length) {
      this.silentTrack = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
      this.stream.addTrack(this.silentTrack.track);
    }

    const videoTrack = input.getVideoTracks()[0];
    if (videoTrack) videoTrack.onended = () => this.onEnded?.();

    this.drawTask = setWorkerInterval(() => this.draw(), 1000 / SCREEN_SHARE_FPS);
  }

  private draw() {
    // Read on every frame: the shared window can be resized mid-share.
    const settings = this.input.getVideoTracks()[0]?.getSettings();
    const width = settings?.width || 1280;
    const height = settings?.height || 720;
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    this.ctx.drawImage(this.video, 0, 0, width, height);
  }

  destroy() {
    this.onEnded = undefined;
    this.drawTask?.clear();
    this.drawTask = undefined;
    this.stream.getTracks().forEach((track) => track.stop());
    this.input.getTracks().forEach((track) => track.stop());
    this.silentTrack?.destroy();
    this.silentTrack = undefined;
    this.video.srcObject = null;
  }
}
